import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import Keycloak from 'keycloak-js';
import { catchError, EMPTY, from, Observable, switchMap, throwError } from 'rxjs';

/** Marks a request as already retried to prevent infinite 401 loops */
const AUTH_RETRIED = new HttpContextToken<boolean>(() => false);

let refreshPromise: Promise<boolean> | null = null;
let isRedirecting = false;

/**
 * Safely logs out strictly once across all concurrent 401 requests.
 * Prevents race conditions and multiple conflicting logout calls.
 */
function logoutOnAuthError(keycloak: Keycloak): void {
  if (isRedirecting) {
    return;
  }
  isRedirecting = true;
  refreshPromise = null;

  keycloak
    .logout({
      redirectUri: window.location.origin,
    })
    .catch(() => {
      try {
        keycloak.clearToken();
      } catch {
        // Ignore any error if Keycloak state was already cleared
      }
      // Fallback in case Keycloak logout fails: hard redirect to app root
      window.location.href = '/';
    });
}

/**
 * Redirects to Keycloak login strictly once across all concurrent 401 requests.
 * Keycloak SSO can return a fresh token seamlessly and return the user to the current page.
 * If login redirect fails, falls back to clean logout.
 */
function redirectToLogin(keycloak: Keycloak): void {
  if (isRedirecting) {
    return;
  }
  isRedirecting = true;
  refreshPromise = null;

  try {
    keycloak.clearToken();
  } catch {
    // Ignore any error if Keycloak state was already cleared
  }

  keycloak
    .login({
      redirectUri: window.location.href,
    })
    .catch(() => {
      // Fallback in case Keycloak login redirect fails: perform clean logout
      isRedirecting = false;
      logoutOnAuthError(keycloak);
    });
}

/**
 * Safely refreshes the Keycloak token.
 * Concurrent 401 requests share the exact same refresh promise,
 * preventing race conditions and multiple conflicting token updates.
 */
function refreshToken(keycloak: Keycloak): Observable<boolean> {
  if (!keycloak.authenticated || !keycloak.refreshToken) {
    return throwError(() => new Error('No active session'));
  }

  if (!refreshPromise) {
    refreshPromise = keycloak
      .updateToken(30)
      .then((refreshed) => {
        if (!refreshed) {
          return keycloak.updateToken(-1);
        }
        return true;
      })
      .catch((err) => {
        refreshPromise = null;
        throw err;
      })
      .finally(() => {
        // Allow in-flight concurrent requests to resolve before resetting
        setTimeout(() => {
          refreshPromise = null;
        }, 500);
      });
  }
  return from(refreshPromise);
}

export const authErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const keycloak = inject(Keycloak);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401) {
        return throwError(() => error);
      }

      // If already retried once and still got 401: a fresh token was already attempted and rejected.
      // The session is invalid or user lacks permissions -> perform logout.
      if (req.context.get(AUTH_RETRIED)) {
        logoutOnAuthError(keycloak);
        return EMPTY;
      }

      // If user is not authenticated (token expired, session lost, or back button navigation to protected page),
      // redirect to login to attempt SSO fresh token recovery, or clean logout.
      if (!keycloak.authenticated) {
        redirectToLogin(keycloak);
        return EMPTY;
      }

      return refreshToken(keycloak).pipe(
        switchMap(() => {
          const authReq = req.clone({
            setHeaders: {
              Authorization: `Bearer ${keycloak.token}`,
            },
            context: req.context.set(AUTH_RETRIED, true),
          });

          return next(authReq);
        }),
        catchError(() => {
          // Refresh token failed -> try login via SSO to obtain a fresh token without losing the current page.
          // If login fails or there is no active SSO session, it falls back to logout.
          redirectToLogin(keycloak);
          return EMPTY;
        })
      );
    })
  );
};
